import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider } from "@/context/AppContext";
import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Work from "@/pages/public/Work";
import { Projects, ProjectDetail } from "@/pages/public/Projects";
import { Articles, ArticleDetail } from "@/pages/public/Articles";
import News from "@/pages/public/News";
import Gallery from "@/pages/public/Gallery";
import { Greetings, GreetingDetail } from "@/pages/public/Greetings";
import Register from "@/pages/public/Register";
import Verify from "@/pages/public/Verify";
import Contact from "@/pages/public/Contact";
import Donate from "@/pages/public/Donate";
import DonateStatus from "@/pages/public/DonateStatus";
import PrintReceipt from "@/pages/public/PrintReceipt";
import CheckStatus from "@/pages/public/CheckStatus";
import ReceiptView from "@/pages/public/ReceiptView";
import ReceiptVerify from "@/pages/public/ReceiptVerify";
import Search from "@/pages/public/Search";
import NotFound from "@/pages/public/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import SuperadminSignup from "@/pages/admin/SuperadminSignup";
import { ForgotPassword, ResetPassword } from "@/pages/admin/PasswordReset";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Content from "@/pages/admin/Content";
import Collections from "@/pages/admin/Collections";
import Participants from "@/pages/admin/Participants";
import Enquiries from "@/pages/admin/Enquiries";
import Donations from "@/pages/admin/Donations";
import GreetingsModule from "@/pages/admin/GreetingsModule";
import PipelineTab from "@/pages/admin/PipelineTab";
import Navigation from "@/pages/admin/Navigation";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import SeoManager from "@/pages/admin/SeoManager";
import MyAccount from "@/pages/admin/MyAccount";
import Users from "@/pages/admin/Users";
import AuditLogs from "@/pages/admin/AuditLogs";
import HeroManager from "@/pages/admin/HeroManager";
import Albums from "@/pages/admin/Albums";
import StudioLayout from "@/pages/studio/StudioLayout";
import StudioHome from "@/pages/studio/StudioHome";
import StudioComposer from "@/pages/studio/StudioComposer";
import StudioLibrary from "@/pages/studio/StudioLibrary";
import StudioCalendar from "@/pages/studio/StudioCalendar";
import StudioDaily from "@/pages/studio/StudioDaily";
import StudioContacts from "@/pages/studio/StudioContacts";
import StudioWhatsApp from "@/pages/studio/StudioWhatsApp";
import StudioSender from "@/pages/studio/StudioSender";
import StudioCampaigns from "@/pages/studio/StudioCampaigns";
import StudioAudit from "@/pages/studio/StudioAudit";
import StudioFacebook from "@/pages/studio/StudioFacebook";
import StudioIntegrations from "@/pages/studio/StudioIntegrations";
import StudioLogs from "@/pages/studio/StudioLogs";
import StudioSettings from "@/pages/studio/StudioSettings";
import StudioTemplates from "@/pages/studio/StudioTemplates";
import { PeopleManager, OrgsManager, QuotesManager } from "@/pages/studio/StudioData";

export const isStudioHost = () =>
  window.location.hostname.startsWith("greetings.") ||
  localStorage.getItem("ssf_studio_host") === "1";

const studioChildren = (
  <>
    <Route index element={<StudioHome />} />
    <Route path="create" element={<StudioComposer />} />
    <Route path="daily" element={<StudioDaily />} />
    <Route path="library" element={<StudioLibrary />} />
    <Route path="templates" element={<StudioTemplates />} />
    <Route path="calendar" element={<StudioCalendar />} />
    <Route path="ai" element={<StudioComposer />} />
    <Route path="quotes" element={<QuotesManager />} />
    <Route path="contacts" element={<StudioContacts />} />
    <Route path="whatsapp" element={<StudioWhatsApp />} />
    <Route path="sender" element={<StudioSender />} />
    <Route path="campaigns" element={<StudioCampaigns />} />
    <Route path="pipeline" element={<PipelineTab />} />
    <Route path="audit" element={<StudioAudit />} />
    <Route path="facebook" element={<StudioFacebook />} />
    <Route path="media" element={<MediaLibrary />} />
    <Route path="people" element={<PeopleManager />} />
    <Route path="orgs" element={<OrgsManager />} />
    <Route path="integrations" element={<StudioIntegrations />} />
    <Route path="logs" element={<StudioLogs />} />
    <Route path="settings" element={<StudioSettings />} />
    <Route path="dashboard" element={<Navigate to="." replace />} />
    <Route path="greetings" element={<Navigate to="library" replace />} />
    <Route path="organizations" element={<Navigate to="orgs" replace />} />
  </>
);

function App() {
  const studioHost = isStudioHost();
  return (
    <AppProvider>
      <BrowserRouter>
        {studioHost ? (
          <Routes>
            <Route path="/login" element={<AdminLogin studio />} />
            <Route path="/" element={<StudioLayout base="" />}>{studioChildren}</Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : (
          <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/work" element={<Work />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<ArticleDetail />} />
            <Route path="/news" element={<News />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/greetings" element={<Greetings />} />
            <Route path="/greetings/:slug" element={<GreetingDetail />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/donate/status" element={<DonateStatus />} />
            <Route path="/print-receipt" element={<PrintReceipt />} />
            <Route path="/donate/check-status" element={<CheckStatus />} />
            <Route path="/donate/receipt/:token" element={<ReceiptView />} />
            <Route path="/donate/receipt/verify/:token" element={<ReceiptVerify />} />
            <Route path="/search" element={<Search />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/superadmin" element={<AdminLogin />} />
          <Route path="/superadmin/login" element={<Navigate to="/superadmin" replace />} />
          <Route path="/superadmin/forgot-password" element={<ForgotPassword />} />
          <Route path="/superadmin/reset-password" element={<ResetPassword />} />
          <Route path="/superadmin/signup" element={<SuperadminSignup />} />
          <Route path="/superadmin/greetings" element={<StudioLayout base="/superadmin/greetings" />}>{studioChildren}</Route>
          <Route path="/greeting-login" element={<AdminLogin greeting />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="content" element={<Content />} />
            <Route path="hero" element={<HeroManager />} />
            <Route path="albums" element={<Albums />} />
            <Route path="collections/:entity" element={<Collections />} />
            <Route path="participants" element={<Participants />} />
            <Route path="enquiries" element={<Enquiries />} />
            <Route path="donations" element={<Donations />} />
            <Route path="greetings" element={<GreetingsModule />} />
            <Route path="navigation" element={<Navigation />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="seo" element={<SeoManager />} />
            <Route path="account" element={<MyAccount />} />
            <Route path="users" element={<Users />} />
            <Route path="audit" element={<AuditLogs />} />
          </Route>
          <Route path="/studio" element={<StudioLayout />}>{studioChildren}</Route>
          </Routes>
        )}
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
